// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'

import { GridRowsProp } from '@mui/x-data-grid-premium'
import { fetchDailyPoolAccountCTOs, fetchDailyPoolOverviewCTOs, fetchPoolEventCTOs } from '@react/fetchers/PoolFetchers'
import { PoolTypeEnum, ReportDataShape } from '@shared/Enums'
import { PoolDynamicAccountCTOSchema } from '@shared/PoolDynamicAccountCTO'
import { PoolDynamicOverviewCTOSchema } from '@shared/PoolDynamicCTO'
import { PoolEventCTO, PoolEventSchema } from '@shared/PoolEventCTO'
import { PoolFlexAccountCTOSchema } from '@shared/PoolFlexAccountCTO'
import { PoolFlexOverviewCTOSchema } from '@shared/PoolFlexCTO'
import { PoolTermAccountCTOSchema } from '@shared/PoolTermAccountCTO'
import { PoolOverviewCTOSchema } from '@shared/PoolTermCTO'
import { EthAddr } from '@shared/SchemaTypes'
import { WithdrawEventTypes } from '@shared/templateApx'
import { ZeroAddress } from 'ethers'
import { logOT } from '@shared/loggingShared'
import { SierraEventSchema } from '@react/sierra/SierraEventCTO'
import { fetchSierraEvents, fetchSierraReserves } from '@react/sierra/sierraFetchers'
import { SierraReservesSchema } from '@react/sierra/SierraReservesCTO'

export interface ReportFetchParams {
  serverApiBaseUrl: string
  restAPIKey: string
  poolAddr?: EthAddr
  lenderAddr?: EthAddr
  startDayStr?: string
  endDayStr?: string
}
export enum ReportSelectorType {
  POOL_ADDR = 'POOL_ADDR',
  LENDER_ADDR = 'LENDER_ADDR',
  DATE_RANGE = 'DATE_RANGE',
  NUMBER_OF_DAYS = 'NUMBER_OF_DAYS',
  REPORT_DEFINITION = 'REPORT_DEFINITION',
}
export enum ReportSelectorRequirement {
  REQUIRED = 'REQUIRED',
  OPTIONAL = 'OPTIONAL',
  REQUIRED_OR_ALL = 'REQUIRED_OR_ALL',
}
export interface ReportDescriptor {
  label: string
  key: string
  selectors: Partial<Record<ReportSelectorType, ReportSelectorRequirement>>
  dataShape: ReportDataShape
  schema: yup.ObjectSchema<any>
  poolTypeFilter?: PoolTypeEnum

  fetcher: (params: ReportFetchParams) => Promise<GridRowsProp>
}

export const REPORT_SIERRA_RESERVES: ReportDescriptor = {
  label: 'Reserves',
  key: 'RESERVES',

  selectors: {},
  poolTypeFilter: undefined,
  dataShape: ReportDataShape.SIERRA_RESERVES,
  schema: SierraReservesSchema,
  fetcher: async (params: ReportFetchParams) => {
    const reserveItems = await fetchSierraReserves(params)

    return reserveItems
  },
}

export const REPORT_SIERRA_POOL_EVENTS: ReportDescriptor = {
  label: 'Transaction Ledger',
  key: 'POOL_EVENTS',

  selectors: {
    [ReportSelectorType.NUMBER_OF_DAYS]: ReportSelectorRequirement.REQUIRED,
  },
  poolTypeFilter: undefined,
  dataShape: ReportDataShape.SIERRA_EVENTS,
  schema: SierraEventSchema,
  fetcher: async (params: ReportFetchParams) => {
    const poolEvents = await fetchSierraEvents(params)

    return poolEvents
  },
}

export const REPORT_FETCH_DESCRIPTOR_POOL_EVENTS: ReportDescriptor = {
  label: 'Transaction Ledger',
  key: 'POOL_EVENTS',

  selectors: {
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED_OR_ALL,
    [ReportSelectorType.NUMBER_OF_DAYS]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.LENDER_ADDR]: ReportSelectorRequirement.OPTIONAL,
    [ReportSelectorType.REPORT_DEFINITION]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: undefined,
  dataShape: ReportDataShape.POOL_EVENTS,
  schema: PoolEventSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, lenderAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr || poolAddr == ZeroAddress) {
      const poolEvents = await fetchPoolEventCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        lenderAddr,
        startDayStr,
        endDayStr
      )
      return poolEvents
    }
    return []
  },
}

export const REPORT_FETCH_DESCRIPTOR_POOL_OVERVIEW_DYNAMIC: ReportDescriptor = {
  label: 'Dynamic - Overview',
  key: 'POOL_OVERVIEW_DYNAMIC',

  selectors: {
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.REPORT_DEFINITION]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: PoolTypeEnum.Dynamic,
  dataShape: ReportDataShape.POOL_OVERVIEW_DYNAMIC,
  schema: PoolDynamicOverviewCTOSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr) {
      const poolEvents = await fetchDailyPoolOverviewCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        startDayStr,
        endDayStr
      )
      return poolEvents
    }
    return []
  },
}

export const REPORT_FETCH_DESCRIPTOR_POOL_OVERVIEW_FLEX: ReportDescriptor = {
  label: 'Flexible Term - Overview',
  key: 'POOL_OVERVIEW_FLEX',

  selectors: {
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.REPORT_DEFINITION]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: PoolTypeEnum.FlexibleRate,
  dataShape: ReportDataShape.POOL_OVERVIEW_FLEX,
  schema: PoolFlexOverviewCTOSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr) {
      const poolEvents = await fetchDailyPoolOverviewCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        startDayStr,
        endDayStr
      )
      return poolEvents
    }
    return []
  },
}

export const REPORT_FETCH_DESCRIPTOR_POOL_OVERVIEW_TERM: ReportDescriptor = {
  label: 'Fixed Term - Overview',
  key: 'POOL_OVERVIEW_Term',

  selectors: {
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.REPORT_DEFINITION]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: PoolTypeEnum.Term,
  dataShape: ReportDataShape.POOL_OVERVIEW_TERM,
  schema: PoolOverviewCTOSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr) {
      const poolEvents = await fetchDailyPoolOverviewCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        startDayStr,
        endDayStr
      )
      return poolEvents
    }
    return []
  },
}

export const REPORT_FETCH_DESCRIPTOR_POOL_ACCOUNT_DYNAMIC: ReportDescriptor = {
  label: 'Dynamic - Daily & Summary',
  key: 'POOL_ACCOUNT_DYNAMIC',

  selectors: {
    [ReportSelectorType.LENDER_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.REPORT_DEFINITION]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: PoolTypeEnum.Dynamic,
  dataShape: ReportDataShape.LENDER_TIME_PERIOD_DYNAMIC,
  schema: PoolDynamicAccountCTOSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, lenderAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr && lenderAddr) {
      const poolEvents = await fetchDailyPoolAccountCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        lenderAddr,
        startDayStr,
        endDayStr
      )
      return poolEvents
    }
    return []
  },
}

export const REPORT_FETCH_DESCRIPTOR_POOL_ACCOUNT_FLEX: ReportDescriptor = {
  label: 'Flexible Term - Daily & Summary',
  key: 'POOL_ACCOUNT_FLEX',

  selectors: {
    [ReportSelectorType.LENDER_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.REPORT_DEFINITION]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: PoolTypeEnum.FlexibleRate,
  dataShape: ReportDataShape.LENDER_TIME_PERIOD_FLEX,
  schema: PoolFlexAccountCTOSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, lenderAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr && lenderAddr) {
      const poolEvents = await fetchDailyPoolAccountCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        lenderAddr,
        startDayStr,
        endDayStr
      )
      return poolEvents
    }
    return []
  },
}

export const REPORT_FETCH_DESCRIPTOR_POOL_ACCOUNT_TERM: ReportDescriptor = {
  label: 'Fixed Term - Daily & Summary',
  key: 'POOL_ACCOUNT_TERM',

  selectors: {
    [ReportSelectorType.LENDER_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.REPORT_DEFINITION]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: PoolTypeEnum.Term,
  dataShape: ReportDataShape.LENDER_TIME_PERIOD_TERM,
  schema: PoolTermAccountCTOSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, lenderAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr && lenderAddr) {
      const poolEvents = await fetchDailyPoolAccountCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        lenderAddr,
        startDayStr,
        endDayStr
      )
      return poolEvents
    }
    return []
  },
}

export const ADMIN_REPORT_DESCRIPTORS: ReportDescriptor[] = [
  REPORT_FETCH_DESCRIPTOR_POOL_OVERVIEW_DYNAMIC,
  REPORT_FETCH_DESCRIPTOR_POOL_OVERVIEW_FLEX,
  REPORT_FETCH_DESCRIPTOR_POOL_OVERVIEW_TERM,
  REPORT_FETCH_DESCRIPTOR_POOL_ACCOUNT_DYNAMIC,
  REPORT_FETCH_DESCRIPTOR_POOL_ACCOUNT_FLEX,
  REPORT_FETCH_DESCRIPTOR_POOL_ACCOUNT_TERM,
  REPORT_FETCH_DESCRIPTOR_POOL_EVENTS,
]

export const LENDER_REPORT_DESCRIPTORS: ReportDescriptor[] = [
  REPORT_FETCH_DESCRIPTOR_POOL_ACCOUNT_DYNAMIC,
  REPORT_FETCH_DESCRIPTOR_POOL_ACCOUNT_FLEX,
  REPORT_FETCH_DESCRIPTOR_POOL_ACCOUNT_TERM,
  REPORT_FETCH_DESCRIPTOR_POOL_EVENTS,
]

export const REPORT_FETCH_DESCRIPTOR_WITHDRAW_EVENTS_ALL_LENDERS: ReportDescriptor = {
  label: 'Transaction Ledger',
  key: 'POOL_EVENTS',

  selectors: {},
  poolTypeFilter: undefined,
  dataShape: ReportDataShape.POOL_EVENTS,
  schema: PoolEventSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, lenderAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr) {
      const poolEvents = await fetchPoolEventCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        lenderAddr,
        startDayStr,
        endDayStr
      )
      const withdrawEvents: PoolEventCTO[] = poolEvents.filter((event: PoolEventCTO) => {
        return WithdrawEventTypes.includes(event.eventType)
      })
      return withdrawEvents
    }
    return []
  },
}

export const REPORT_FETCH_DESCRIPTOR_TRANSACTION_LIST: ReportDescriptor = {
  label: 'Transaction Ledger',
  key: 'POOL_EVENTS',

  selectors: {
    [ReportSelectorType.NUMBER_OF_DAYS]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: undefined,
  dataShape: ReportDataShape.POOL_EVENTS,
  schema: PoolEventSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, lenderAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr) {
      const poolEvents = await fetchPoolEventCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        lenderAddr,
        startDayStr,
        endDayStr
      )
      logOT.info('REPORT_FETCH_DESCRIPTOR_TRANSACTION_LIST', params, poolEvents)
      return poolEvents
    }
    logOT.info('REPORT_FETCH_DESCRIPTOR_TRANSACTION_LIST EMPTY')
    return []
  },
}
